import { estThemeRegie, THEME_REGIE_DEFAUT, THEMES_REGIE, type ThemeRegie } from '@artosera/shared';

/**
 * Visual identities are independent of light/dark colour and fair mode. The
 * list is shared with the API and the console (`THEMES_REGIE`, lot C).
 */
export const DESIGN_IDS = THEMES_REGIE;
export type DesignId = ThemeRegie;
/**
 * Thèmes proposés dans le sélecteur. Les autres restent valides (CSS, API,
 * thèmes déjà appliqués) mais ne sont plus offerts au choix.
 */
export const DESIGNS_PROPOSES: readonly DesignId[] = [
  'halo',
  'monolithe',
  'spatial',
  'liquid-glass',
  'verre-de-jour',
  'verre-de-nuit',
];
/** Les thèmes à afficher : les proposés, plus le thème courant s'il est masqué. */
export function designsAffiches(courant: DesignId): readonly DesignId[] {
  return DESIGNS_PROPOSES.includes(courant) ? DESIGNS_PROPOSES : [...DESIGNS_PROPOSES, courant];
}
export const DEFAULT_DESIGN: DesignId = THEME_REGIE_DEFAUT;
export function isDesignId(value: unknown): value is DesignId {
  return estThemeRegie(value);
}
export function isHorizontalDesign(design: DesignId): boolean {
  return (
    design === 'structure' ||
    design === 'collection' ||
    design === 'brut' ||
    design === 'aurore' ||
    design === 'blanc' ||
    design === 'chrome' ||
    design === 'verre-de-jour' ||
    design === 'verre-de-nuit'
  );
}

export const designNames: Record<DesignId, string> = {
  halo: 'Halo',
  structure: 'Structure',
  collection: 'Collection',
  brut: 'Brut',
  atelier: 'Atelier',
  aurore: 'Aurore',
  monolithe: 'Monolithe',
  blanc: 'Blanc',
  spatial: 'Spatial',
  chrome: 'Chrome',
  'liquid-glass': 'Liquid Glass',
  'verre-de-jour': 'Verre de jour',
  'verre-de-nuit': 'Verre de nuit',
};
export const designDescriptions: Record<'fr' | 'en', Record<DesignId, string>> = {
  fr: {
    halo: 'Surfaces douces, navigation latérale et priorités en clair.',
    structure: 'Navigation horizontale, listes de travail et inspecteur précis.',
    collection: 'Inventaire visuel, bandeau contrasté et œuvres au premier plan.',
    brut: 'Papier kraft, orange signal, filets épais et ombres franches.',
    atelier: 'Composition éditoriale, palette minérale et espace de travail latéral.',
    aurore: 'Lumière ambiante, verre dépoli et navigation en pilule flottante.',
    monolithe: 'Espace, filets fins, accent champagne et navigation latérale sobre.',
    blanc: 'Mur blanc chaud, graphite et œuvres accrochées dans un espace calme.',
    spatial: 'Verre neutre, profondeur douce et rail latéral flottant.',
    chrome: 'Métal satiné, typographie étendue et sélecteur horizontal irisé.',
    'liquid-glass':
      'Verre liquide : barres flottantes translucides, reflets de bord et œuvres nettes au premier plan.',
    'verre-de-jour':
      'Salle de jour : capsules de verre clair, halos tirés des œuvres et accent mauve doux.',
    'verre-de-nuit':
      'Îlots de verre sombres, lueurs violettes et capsule de navigation flottante, la galerie la nuit.',
  },
  en: {
    halo: 'Soft surfaces, sidebar navigation and clear daily priorities.',
    structure: 'Horizontal navigation, working lists and a precise inspector.',
    collection: 'A visual inventory, contrasting header and artwork in focus.',
    brut: 'Kraft paper, signal orange, thick rules and hard shadows.',
    atelier: 'Editorial composition, a mineral palette and a sidebar workspace.',
    aurore: 'Ambient light, frosted glass and floating pill navigation.',
    monolithe: 'Space, fine rules, a champagne accent and a quiet sidebar.',
    blanc: 'Warm white walls, graphite and artwork hanging in a quiet space.',
    spatial: 'Neutral glass, soft depth and a floating sidebar rail.',
    chrome: 'Satin metal, wide typography and an iridescent horizontal selector.',
    'liquid-glass':
      'Liquid glass: floating translucent bars, edge highlights and sharp artwork up front.',
    'verre-de-jour':
      'Daylit gallery: pale glass capsules, halos drawn from the artwork and a soft mauve accent.',
    'verre-de-nuit':
      'Dark glass islands, violet glows and a floating navigation capsule: the gallery at night.',
  },
};
